import { ChangeEvent, FC, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { TablePagination } from "components/paginations/TablePagination"
import { DUPLICATE_SEVERITY_OPTIONS, DUPLICATE_STATUS_OPTIONS } from "components/risk/constants"
import { DuplicateMatchModal } from "components/risk/review/DuplicateMatchModal"
import { Column, DataTable } from "components/tables/DataTable"

import { useTimezone } from "hooks/useTimezone"

import { riskService } from "services/risk/risk.service"
import { DuplicateMatchesFilters, GlobalDuplicateMatch } from "services/risk/types"

const buildColumns = (
  onSelect: (match: GlobalDuplicateMatch) => void,
  t: any,
  formatDate: (d: string | Date) => string
): Column<GlobalDuplicateMatch>[] => [
  {
    header: t("risk.duplicates.colPlayerA"),
    render: (row) => <span className="text-sm font-medium">{row.player_a.username ?? row.player_a.id}</span>
  },
  {
    header: t("risk.duplicates.colPlayerB"),
    render: (row) => <span className="text-sm font-medium">{row.player_b.username ?? row.player_b.id}</span>
  },
  {
    header: t("risk.duplicates.colSimilarity"),
    render: (row) => <span className="font-mono text-sm">{Math.round(row.similarity_score * 100)}%</span>
  },
  {
    header: t("risk.duplicates.colSeverity"),
    render: (row) => {
      const color = row.severity === "TOO_HIGH" ? "text-error" : row.severity === "HIGH" ? "text-warning" : "text-base-content/70"
      return <span className={`text-xs font-semibold uppercase ${color}`}>{row.severity.replace("_", " ")}</span>
    }
  },
  {
    header: t("risk.duplicates.colStatus"),
    render: (row) => {
      const badge = row.review_status === "CONFIRMED" ? "badge-error" : row.review_status === "DISMISSED" ? "badge-ghost" : "badge-warning"
      return <span className={`badge badge-sm ${badge}`}>{row.review_status}</span>
    }
  },
  {
    header: t("risk.duplicates.colDetected"),
    render: (row) => <span className="text-xs text-base-content/60">{formatDate(row.detected_at)}</span>
  },
  {
    header: "",
    align: "right",
    render: (row) => (
      <button className="btn btn-xs btn-ghost" onClick={() => onSelect(row)}>
        {t("risk.duplicates.reviewBtn")}
      </button>
    )
  }
]

export const DuplicatesReviewTab: FC = () => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { formatDate } = useTimezone()
  const [matches, setMatches] = useState<GlobalDuplicateMatch[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [selectedMatch, setSelectedMatch] = useState<GlobalDuplicateMatch | null>(null)

  const [filters, setFilters] = useState<DuplicateMatchesFilters>({
    page: 1,
    pageSize: 20,
    status: "PENDING"
  })

  const updateFilter = <K extends keyof DuplicateMatchesFilters>(field: K, value: DuplicateMatchesFilters[K]) =>
    setFilters((f) => ({ ...f, page: 1, [field]: value }))

  const loadMatches = useCallback(async () => {
    setLoading(true)
    try {
      const res = await riskService.getDuplicateMatches(filters)
      if (res) {
        setMatches(res.data)
        setTotal(res.total)
        setTotalPages(res.total_pages)
      }
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadMatches()
  }, [loadMatches])

  const handleReview = async (matchId: string, decision: string) => {
    await riskService.reviewDuplicateMatch(matchId, decision)
    toast.success(String(decision === "CONFIRMED" ? t("risk.duplicates.toastConfirmed") : t("risk.duplicates.toastDismissed")))
    setSelectedMatch(null)
    loadMatches()
  }

  const columns = buildColumns(setSelectedMatch, t, formatDate)

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-40">
          <DropdownMenu
            placeholder={t("risk.duplicates.filterStatus")}
            options={DUPLICATE_STATUS_OPTIONS}
            value={filters.status ?? ""}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => updateFilter("status", e.target.value || undefined)}
          />
        </div>
        <div className="w-40">
          <DropdownMenu
            placeholder={t("risk.duplicates.filterSeverity")}
            options={DUPLICATE_SEVERITY_OPTIONS}
            value={filters.severity ?? ""}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => updateFilter("severity", e.target.value || undefined)}
          />
        </div>
        <span className="text-xs text-base-content/50 ml-auto">
          {t("risk.duplicates.matchCount", { count: total.toLocaleString() } as any)}
        </span>
      </div>

      {/* Table */}
      {loading && matches.length === 0 ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : (
        <DataTable rows={matches} columns={columns} rowKey={(m) => m.id} />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <TablePagination page={filters.page} totalPages={totalPages} onJump={(p) => setFilters((f) => ({ ...f, page: p }))} />
      )}

      {/* Modal */}
      {selectedMatch && <DuplicateMatchModal match={selectedMatch} onClose={() => setSelectedMatch(null)} onReview={handleReview} />}
    </div>
  )
}
